import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";

export interface AddCustomerFormData {
  name: string;
  description: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  notes: string;
}

export interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddCustomerFormData) => void;
}

const initialCustomerFormData: AddCustomerFormData = {
  name: "",
  description: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  notes: "",
};

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<AddCustomerFormData>(
    initialCustomerFormData
  );

  const [errors, setErrors] = useState<{
    name?: string;
    contactEmail?: string;
  }>({});

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialCustomerFormData);
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {
      name?: string;
      contactEmail?: string;
    } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Customer name is required";
    }

    if (
      formData.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
    ) {
      newErrors.contactEmail = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create customer</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customer-name">
                Customer Name<span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                id="customer-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Hutchinsons"
                required
                aria-required="true"
                aria-invalid={errors.name ? "true" : "false"}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              {errors.name && (
                <p id="name-error" className="text-sm text-destructive">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer-description">Description</Label>
              <Textarea
                id="customer-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="e.g. Contact info, company size & details"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-name">Contact Name</Label>
              <Input
                type="text"
                id="contact-name"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                placeholder="e.g. John Smith"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-email">Contact Email</Label>
              <Input
                type="email"
                id="contact-email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                placeholder="e.g. john.smith@example.com"
                aria-describedby={
                  errors.contactEmail ? "email-error" : undefined
                }
                aria-invalid={errors.contactEmail ? "true" : "false"}
              />
              {errors.contactEmail && (
                <p id="email-error" className="text-sm text-destructive">
                  {errors.contactEmail}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-phone">Contact Phone</Label>
              <Input
                type="tel"
                id="contact-phone"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleChange}
                placeholder="e.g. +44 123 456 7890"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer-notes">Notes</Label>
              <Textarea
                id="customer-notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Provide additional details or context"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" className="w-full sm:w-auto">
              Create Customer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomerModal;
